
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Loader2, Upload, Image as ImageIcon } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface LogoUploaderProps {
  logoUrl: string | null | undefined;
  onLogoUpload: (logoUrl: string) => void;
}

const LogoUploader: React.FC<LogoUploaderProps> = ({
  logoUrl,
  onLogoUpload
}) => {
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(logoUrl || null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const { toast } = useToast();

  const validateImage = (file: File): Promise<boolean> => {
    return new Promise((resolve) => {
      const img = document.createElement('img');
      img.src = URL.createObjectURL(file);
      
      img.onload = () => {
        URL.revokeObjectURL(img.src);
        if (img.width > 400 || img.height > 100) {
          setUploadError('Image dimensions should be 400x100px or smaller');
          resolve(false);
        } else {
          resolve(true);
        }
      };
      
      img.onerror = () => {
        URL.revokeObjectURL(img.src);
        setUploadError('Failed to load image');
        resolve(false);
      };
    });
  };

  // Handle logo file selection
  const handleLogoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    setUploadError(null);
    
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      
      // File type validation
      if (!file.type.match('image.*')) {
        setUploadError('Please select an image file (PNG, JPG, JPEG, GIF)');
        return;
      }
      
      // File size validation (max 500KB)
      if (file.size > 500 * 1024) {
        setUploadError('Image size should be less than 500KB');
        return;
      }
      
      // Dimension validation
      const isValidDimensions = await validateImage(file);
      if (!isValidDimensions) {
        return;
      }
      
      setLogoFile(file);
      
      // Create a preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  // Handle logo upload
  const handleUploadLogo = async () => {
    if (!logoFile) return;
    
    setIsUploading(true);
    setUploadError(null);
    
    try {
      // Mock upload functionality - in a real app, this would be an API call
      // Simulating upload delay
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Return the logo URL to parent component
      onLogoUpload(logoPreview as string);
      
      toast({
        title: "Logo uploaded successfully",
        description: "Your website logo has been updated",
      });
      
      setLogoFile(null);
      
    } catch (error) {
      console.error('Error uploading logo:', error);
      setUploadError('Failed to upload logo. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="space-y-4">
      {uploadError && (
        <div className="bg-red-50 text-red-700 p-3 rounded-md text-sm">
          {uploadError}
        </div>
      )}
      
      <div className="border rounded-md p-4 flex items-center justify-center bg-gray-50 h-40">
        {logoPreview ? (
          <img 
            src={logoPreview} 
            alt="Website Logo" 
            className="max-h-32 max-w-full object-contain" 
          />
        ) : (
          <div className="text-center text-gray-400">
            <ImageIcon className="mx-auto h-12 w-12 mb-2" />
            <p>No logo uploaded</p>
          </div>
        )}
      </div>
      
      <div className="grid grid-cols-1 gap-4">
        <Input
          type="file"
          accept="image/*"
          onChange={handleLogoChange}
          className="mt-2"
        />
        <p className="text-xs text-gray-500">
          Accepted formats: PNG, JPG, JPEG, GIF. Maximum size: 500KB.
          Maximum dimensions: 400x100px.
        </p>
        <Button 
          onClick={handleUploadLogo}
          disabled={!logoFile || isUploading}
          className="w-full sm:w-auto"
        >
          {isUploading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Uploading...
            </>
          ) : (
            <>
              <Upload className="mr-2 h-4 w-4" /> Upload Logo
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

export default LogoUploader;
